/**
 * 모빌리티 허브의 **필터 축**(배기량 외) — 타입·라이딩·변속·제조사. 순수 모듈.
 *
 * 값은 전부 `products.specifications` jsonb 에서 온다:
 * `{ kind: "bike", maker, type, transmission, rideStyles?, … }`
 * (`bike-specs.ts` 가 상품 상세 표를 그릴 때 읽는 것과 같은 필드).
 *
 * 라벨은 `bike-classes.ts` 와 같은 이유로 i18n 메시지가 아니라 **이 파일의 상수**다 —
 * 키 집합이 파트너 데이터에 묶여 있어 5개 언어 파일을 동시에 고치지 않게 한다.
 * (알약 아래 한 줄 보조 문구만 i18n `search.hub.typeSub.*` · `rideSub.*`.)
 */

import type { Locale } from "@/i18n/routing";
import {
  BIKE_CLASSES,
  getBikeClass,
  type BikeClassKey,
  type BikeClassKeyOrUnknown,
} from "./bike-classes";
import { BIKE_MAKER_KEYS, type BikeMaker } from "./bike-makers";
import { matchesParty, type PartySize } from "./camp-party";

// ── 키 집합 ────────────────────────────────────────────────────────────────

export const BIKE_TYPE_KEYS = [
  "scooter",
  "naked",
  "cruiser",
  "street",
  "tourer",
  "sport",
  "offroad",
  "other",
] as const;
export type BikeType = (typeof BIKE_TYPE_KEYS)[number];

export const RIDE_STYLE_KEYS = [
  "city",
  "day",
  "long",
  "twoUp",
  "adventure",
] as const;
export type RideStyle = (typeof RIDE_STYLE_KEYS)[number];

export const TRANSMISSION_KEYS = ["AT", "MT"] as const;
export type Transmission = (typeof TRANSMISSION_KEYS)[number];

const BIKE_TYPE_SET = new Set<string>(BIKE_TYPE_KEYS);
const RIDE_STYLE_SET = new Set<string>(RIDE_STYLE_KEYS);

// ── 라벨 (5개 언어) ─────────────────────────────────────────────────────────

export const BIKE_TYPE_LABELS: Readonly<Record<BikeType, Record<Locale, string>>> = {
  scooter: {
    ko: "스쿠터",
    en: "Scooter",
    ja: "スクーター",
    "zh-CN": "踏板车",
    "zh-TW": "速克達",
  },
  naked: {
    ko: "네이키드",
    en: "Naked",
    ja: "ネイキッド",
    "zh-CN": "街车",
    "zh-TW": "街車",
  },
  cruiser: {
    ko: "크루저",
    en: "Cruiser",
    ja: "クルーザー",
    "zh-CN": "巡航车",
    "zh-TW": "巡航車",
  },
  street: {
    ko: "스트리트",
    en: "Street",
    ja: "ストリート",
    "zh-CN": "街道车",
    "zh-TW": "街道車",
  },
  tourer: {
    ko: "투어링",
    en: "Touring",
    ja: "ツーリング",
    "zh-CN": "旅行车",
    "zh-TW": "旅行車",
  },
  sport: {
    ko: "스포츠",
    en: "Sport",
    ja: "スポーツ",
    "zh-CN": "运动车",
    "zh-TW": "運動車",
  },
  offroad: {
    ko: "어드벤처",
    en: "Adventure",
    ja: "アドベンチャー",
    "zh-CN": "探险车",
    "zh-TW": "探險車",
  },
  other: {
    ko: "기타",
    en: "Other",
    ja: "その他",
    "zh-CN": "其他",
    "zh-TW": "其他",
  },
};

export const RIDE_STYLE_LABELS: Readonly<Record<RideStyle, Record<Locale, string>>> = {
  city: {
    ko: "시내 주행",
    en: "City riding",
    ja: "街乗り",
    "zh-CN": "市区骑行",
    "zh-TW": "市區騎乘",
  },
  day: {
    ko: "당일 투어",
    en: "Day trip",
    ja: "日帰りツーリング",
    "zh-CN": "一日游",
    "zh-TW": "一日遊",
  },
  long: {
    ko: "장거리 투어링",
    en: "Long-distance touring",
    ja: "ロングツーリング",
    "zh-CN": "长途旅行",
    "zh-TW": "長途旅行",
  },
  twoUp: {
    ko: "2인 투어링",
    en: "Two-up touring",
    ja: "タンデムツーリング",
    "zh-CN": "双人骑行",
    "zh-TW": "雙載旅行",
  },
  adventure: {
    ko: "어드벤처",
    en: "Adventure",
    ja: "アドベンチャー",
    "zh-CN": "探险",
    "zh-TW": "探險",
  },
};

/**
 * 제조사 표기 — 원문은 파트너 데이터의 라틴 문자("Honda"). 사전에 없으면 원문 그대로.
 * 키는 소문자 정규화.
 */
const MAKER_LABELS: Readonly<Record<string, Record<Locale, string>>> = {
  honda: {
    ko: "혼다",
    en: "Honda",
    ja: "Honda",
    "zh-CN": "本田",
    "zh-TW": "本田",
  },
  yamaha: {
    ko: "야마하",
    en: "Yamaha",
    ja: "Yamaha",
    "zh-CN": "雅马哈",
    "zh-TW": "山葉",
  },
  suzuki: {
    ko: "스즈키",
    en: "Suzuki",
    ja: "Suzuki",
    "zh-CN": "铃木",
    "zh-TW": "鈴木",
  },
  kawasaki: {
    ko: "가와사키",
    en: "Kawasaki",
    ja: "Kawasaki",
    "zh-CN": "川崎",
    "zh-TW": "川崎",
  },
};

function pick(map: Record<Locale, string>, locale: string): string {
  const dict = map as Record<string, string>;
  return dict[locale] ?? dict.ko ?? Object.values(dict)[0] ?? "";
}

export function bikeTypeLabel(key: BikeType, locale: string): string {
  return pick(BIKE_TYPE_LABELS[key], locale);
}

export function rideStyleLabel(key: RideStyle, locale: string): string {
  return pick(RIDE_STYLE_LABELS[key], locale);
}

/** 제조사 표시명 — 사전에 없으면 원문(trim). 비면 null. */
export function makerLabel(
  raw: string | null | undefined,
  locale: string,
): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const known = MAKER_LABELS[trimmed.toLowerCase()];
  return known ? pick(known, locale) : trimmed;
}

// ── specifications → 파싱 ───────────────────────────────────────────────────

/** 허브 행·필터가 읽는 상품 단위 값 (`HubProduct` 의 부분집합). */
export interface BikeFacets {
  maker: string | null;
  bikeType: BikeType | null;
  transmission: Transmission | null;
  rideStyles: RideStyle[];
}

export const EMPTY_FACETS: Readonly<BikeFacets> = Object.freeze({
  maker: null,
  bikeType: null,
  transmission: null,
  rideStyles: [],
});

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** 알려진 타입 키만 — 나머지(오타·미정)는 null (필터에서 "타입 없음"으로 취급). */
export function parseBikeType(value: unknown): BikeType | null {
  const key = text(value);
  return key && BIKE_TYPE_SET.has(key) ? (key as BikeType) : null;
}

export function parseTransmission(value: unknown): Transmission | null {
  const key = text(value)?.toUpperCase();
  return key === "AT" || key === "MT" ? key : null;
}

/** 배열이 아니거나 비면 [] — 알려진 키만, 중복 제거, `RIDE_STYLE_KEYS` 순서. */
export function parseRideStyles(value: unknown): RideStyle[] {
  if (!Array.isArray(value)) return [];
  const present = new Set<string>();
  for (const item of value) {
    const key = text(item);
    if (key && RIDE_STYLE_SET.has(key)) present.add(key);
  }
  return RIDE_STYLE_KEYS.filter((key) => present.has(key));
}

/**
 * `products.specifications` → 허브 축 값. `kind !== "bike"` 이거나 null 이면 전부 비움.
 * 미래 필드(`rideStyles`)는 있으면 읽고 없으면 [] — 부재를 허용한다.
 */
export function parseBikeFacets(specs: unknown): BikeFacets {
  if (!specs || typeof specs !== "object") return { ...EMPTY_FACETS };
  const record = specs as Record<string, unknown>;
  if (record.kind !== "bike") return { ...EMPTY_FACETS };
  return {
    maker: text(record.maker),
    bikeType: parseBikeType(record.type),
    transmission: parseTransmission(record.transmission),
    rideStyles: parseRideStyles(record.rideStyles),
  };
}

// ── 필터 축 상태 ─────────────────────────────────────────────────────────────

/**
 * 허브 필터 축. 배기량은 단일 선택(기존 `bclass` 의미 유지), 제조사·타입·라이딩은 다중,
 * 변속은 단일. 인원(`parties`)은 캠핑 허브 전용 다중 선택(10-05) — 바이크 축과 섞이지
 * 않도록 허브가 종류별로 반대쪽 축을 비워서 넘긴다(StationHub).
 */
export interface HubAxes {
  classKey: string | null;
  makers: BikeMaker[];
  types: BikeType[];
  rides: RideStyle[];
  trans: Transmission | null;
  parties: PartySize[];
}

export const EMPTY_AXES: Readonly<HubAxes> = Object.freeze({
  classKey: null,
  makers: [],
  types: [],
  rides: [],
  trans: null,
  parties: [],
});

export function isAxesEmpty(axes: HubAxes): boolean {
  return (
    !axes.classKey &&
    axes.makers.length === 0 &&
    axes.types.length === 0 &&
    axes.rides.length === 0 &&
    !axes.trans &&
    axes.parties.length === 0
  );
}

/** 상단 "필터 N" 배지에 더할 개수 — 제조사 수 + 타입 수 + 라이딩 수 + 변속 1 + 배기량 1 + 인원 수. */
export function countActiveAxes(axes: HubAxes): number {
  return (
    (axes.classKey ? 1 : 0) +
    axes.makers.length +
    axes.types.length +
    axes.rides.length +
    (axes.trans ? 1 : 0) +
    axes.parties.length
  );
}

/** 필터 술어가 읽는 상품 값 = 배기량 클래스 + 제원 축 + 제조사 키. */
export interface ProductFacets extends BikeFacets {
  classKey: BikeClassKeyOrUnknown;
  /**
   * 제조사 축 키(`bike-makers.resolveBikeMaker` — 원문 이름 접두 우선). 없으면 제조사
   * 축이 켜졌을 때 빠진다. 선택 필드 — 제원 `maker`(원문 문자열)와는 별개다.
   */
  makerKey?: BikeMaker | null;
  /** 캠핑 텐트 세트 인원(`camp-party.resolvePartySize`). 바이크·인원 모름이면 없음. */
  partySize?: PartySize | null;
}

/**
 * 한 상품이 모든 축을 만족하는가 — **축 사이는 AND, 축 안은 OR**.
 * 라이딩은 상품의 `rideStyles` 중 하나라도 선택에 들어 있으면 통과.
 */
export function matchesAxes(facets: ProductFacets, axes: HubAxes): boolean {
  if (axes.classKey && facets.classKey !== axes.classKey) return false;
  if (
    axes.makers.length > 0 &&
    (!facets.makerKey || !axes.makers.includes(facets.makerKey))
  ) {
    return false;
  }
  if (
    axes.types.length > 0 &&
    (!facets.bikeType || !axes.types.includes(facets.bikeType))
  ) {
    return false;
  }
  if (
    axes.rides.length > 0 &&
    !facets.rideStyles.some((style) => axes.rides.includes(style))
  ) {
    return false;
  }
  if (axes.trans && facets.transmission !== axes.trans) return false;
  if (!matchesParty(facets.partySize, axes.parties)) return false;
  return true;
}

/**
 * 데이터셋에 실제로 있는 축 값 — 알약을 그릴지·옵션에 무엇을 넣을지의 근거.
 * `makers`·`types`·`rides` 가 비면 그 알약을 숨기고, `trans` 가 2개 미만이면 변속
 * 알약을 숨긴다.
 */
export interface PresentFacets {
  makers: BikeMaker[];
  types: BikeType[];
  rides: RideStyle[];
  trans: Transmission[];
}

export function collectPresentFacets(
  facets: Iterable<
    Pick<BikeFacets, "bikeType" | "transmission" | "rideStyles"> &
      Pick<ProductFacets, "makerKey">
  >,
): PresentFacets {
  const makers = new Set<BikeMaker>();
  const types = new Set<BikeType>();
  const rides = new Set<RideStyle>();
  const trans = new Set<Transmission>();
  for (const f of facets) {
    if (f.makerKey) makers.add(f.makerKey);
    if (f.bikeType) types.add(f.bikeType);
    if (f.transmission) trans.add(f.transmission);
    for (const style of f.rideStyles) rides.add(style);
  }
  return {
    makers: BIKE_MAKER_KEYS.filter((key) => makers.has(key)),
    types: BIKE_TYPE_KEYS.filter((key) => types.has(key)),
    rides: RIDE_STYLE_KEYS.filter((key) => rides.has(key)),
    trans: TRANSMISSION_KEYS.filter((key) => trans.has(key)),
  };
}

// ── 배기량 범위 배지 ─────────────────────────────────────────────────────────

/**
 * 지점 행의 배기량 요약. 클래스가 3개 이하면 라벨을 " · " 로 나열("~125cc · ~250cc"),
 * 넘으면 최저 ~ 최고("~50cc ~ 401cc~"). unknown 은 제외, 없으면 "".
 */
export function formatClassRange(
  keys: Iterable<BikeClassKeyOrUnknown>,
  locale: string,
): string {
  const present = new Set<BikeClassKey>();
  for (const key of keys) {
    if (key !== "unknown" && getBikeClass(key)) present.add(key);
  }
  const ordered = BIKE_CLASSES.filter((cls) => present.has(cls.key));
  if (ordered.length === 0) return "";
  const label = (cls: (typeof BIKE_CLASSES)[number]) => pick(cls.label, locale);
  if (ordered.length <= 3) return ordered.map(label).join(" · ");
  return `${label(ordered[0])} ~ ${label(ordered[ordered.length - 1])}`;
}
